import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import ChartProvider from '../../../charts/echarts/ChartProvider'
import ChartColorProvider from '../../../charts/echarts/ColorProvider'
import AxisX from '../../../charts/echarts/AxisX'
import AxisY from '../../../charts/echarts/AxisY'
import Tooltip from '../../../charts/echarts/Tooltip'
import ComponentBar from '../../../charts/echarts/ComponentBar'
import ComponentLine from '../../../charts/echarts/ComponentLine'
import ComponentScatter from '../../../charts/echarts/ComponentScatter'
import useBlueprintData from '../hooks/useBlueprintData'
import { buildLegendConfig, buildLabelConfig, EMPTY_OBJ } from './chartLayoutUtils'

const SERIES_COMPONENT_MAP = {
  bar: ComponentBar,
  line: ComponentLine,
  scatter: ComponentScatter
}

// TODO: sizeAccessor for scatter series is collected by the admin editor but not
// yet extracted here. To support bubble charts, produce [x, y, size] triples
// when a series has sizeAccessor defined, and pass symbolSize as a function to
// ComponentScatter so ECharts can read the third dimension.
function useSeriesData (data, xAccessor, series) {
  return useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return {}

    const result = {}
    for (const s of series) {
      result[s.seriesId] = data.map(row => {
        const x = xAccessor ? get(row, xAccessor) : null
        const y = get(row, s.yAccessor)
        return [x, y]
      })
    }
    return result
  }, [data, xAccessor, series])
}

/**
 * Renders a composable axis chart (bar, line, scatter).
 * Multiple series compose onto shared X/Y axes within a single ChartProvider.
 */
function AxisChartLayout ({ blueprintCode, config, data, parameters }) {
  const {
    height = 400,
    colorTheme = 'primary',
    xAxis,
    yAxes = [],
    tooltip = {},
    series = [],
    display = {},
    overrides = null
  } = config

  const { label: displayLabel = EMPTY_OBJ, legend: displayLegend = EMPTY_OBJ, grid: displayGrid = EMPTY_OBJ } = display

  const legendConfig = buildLegendConfig(displayLegend)

  const labelConfig = buildLabelConfig(displayLabel)

  const primarySeries = useMemo(
    () => series.filter(s => !s.purposeCode || s.purposeCode === config.purposeCode),
    [series, config.purposeCode]
  )
  const overrideSeries = useMemo(
    () => series.filter(s => s.purposeCode && s.purposeCode !== config.purposeCode),
    [series, config.purposeCode]
  )

  const primarySeriesData = useSeriesData(data, xAxis?.accessor, primarySeries)

  return (
    <ChartColorProvider theme={colorTheme}>
      <ChartProvider height={height} loadingTemplate='axis' grid={displayGrid} {...(legendConfig && { legend: legendConfig })} {...(overrides || {})}>
        {xAxis && (
          <AxisX
            type={xAxis.type || 'category'}
            position={xAxis.position || 'bottom'}
            format={xAxis.format || 'number'}
            {...(xAxis.name && { name: xAxis.name })}
            {...(xAxis.boundaryGap != null && { boundaryGap: xAxis.boundaryGap })}
            {...((xAxis.labelRotate != null || xAxis.labelInterval != null) && {
              axisLabel: {
                ...(xAxis.labelRotate != null && { rotate: xAxis.labelRotate }),
                ...(xAxis.labelInterval != null && { interval: xAxis.labelInterval })
              }
            })}
          />
        )}

        {yAxes.map((yAxis, idx) => (
          <AxisY
            key={yAxis.axisId}
            axisId={yAxis.axisId}
            type={yAxis.type || 'value'}
            position={yAxis.position || 'left'}
            format={yAxis.format || 'number'}
            padFactor={yAxis.padFactor || 0}
            {...(yAxis.name && { name: yAxis.name })}
            {...(yAxis.labelRotate != null && { axisLabel: { rotate: yAxis.labelRotate } })}
          />
        ))}

        <Tooltip
          trigger={tooltip.trigger || 'axis'}
          {...(tooltip.valueFormat && { valueFormat: tooltip.valueFormat })}
        />

        {primarySeries.map(s => {
          const Component = SERIES_COMPONENT_MAP[s.type]
          if (!Component) return null
          return (
            <Component
              key={s.seriesId}
              seriesId={s.seriesId}
              name={s.name}
              data={primarySeriesData[s.seriesId] || []}
              yAxisIndex={s.yAxisIndex || 0}
              {...(s.color && { itemStyle: { color: s.color } })}
              {...buildTypeSpecificProps(s)}
              label={labelConfig}
              {...(s.overrides || {})}
            />
          )
        })}

        {overrideSeries.map(s => (
          <OverrideSeriesRenderer
            key={s.seriesId}
            blueprintCode={blueprintCode}
            seriesConfig={s}
            display={display}
            xAccessor={xAxis?.accessor}
            parameters={parameters}
          />
        ))}
      </ChartProvider>
    </ChartColorProvider>
  )
}

function buildTypeSpecificProps (s) {
  const props = {}
  if (s.type === 'bar') {
    if (s.stack) props.stack = s.stack
    if (s.barWidth) props.barWidth = s.barWidth
  }
  if (s.type === 'line') {
    if (s.smooth) props.smooth = true
    if (s.areaStyle) props.areaStyle = {}
    if (s.step) props.step = s.step
  }
  if (s.type === 'scatter') {
    if (s.symbolSize) props.symbolSize = s.symbolSize
  }
  return props
}

/**
 * Fetches data independently for a series that overrides the chart-level purposeCode.
 */
function OverrideSeriesRenderer ({ blueprintCode, seriesConfig, display = {}, xAccessor, parameters }) {
  const { data, isLoading } = useBlueprintData(
    blueprintCode,
    seriesConfig.purposeCode,
    { parameters, pageSize: 1000 }
  )

  const displayLabel = display.label ?? EMPTY_OBJ
  const overrideLabelConfig = buildLabelConfig(displayLabel)

  const chartData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return []
    return data.map(row => {
      const x = xAccessor ? get(row, xAccessor) : null
      const y = get(row, seriesConfig.yAccessor)
      return [x, y]
    })
  }, [data, xAccessor, seriesConfig.yAccessor])

  if (isLoading) return null

  const Component = SERIES_COMPONENT_MAP[seriesConfig.type]
  if (!Component) return null

  return (
    <Component
      seriesId={seriesConfig.seriesId}
      name={seriesConfig.name}
      data={chartData}
      yAxisIndex={seriesConfig.yAxisIndex || 0}
      {...(seriesConfig.color && { itemStyle: { color: seriesConfig.color } })}
      {...buildTypeSpecificProps(seriesConfig)}
      label={overrideLabelConfig}
      {...(seriesConfig.overrides || {})}
    />
  )
}

OverrideSeriesRenderer.propTypes = {
  blueprintCode: PropTypes.string.isRequired,
  seriesConfig: PropTypes.object.isRequired,
  display: PropTypes.object,
  xAccessor: PropTypes.string,
  parameters: PropTypes.object
}

AxisChartLayout.propTypes = {
  blueprintCode: PropTypes.string.isRequired,
  config: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  parameters: PropTypes.object
}

export default AxisChartLayout
